import { useState, useRef } from 'react';
import { Hammer, Check, Square, AlertTriangle, TrendingUp, DollarSign } from 'lucide-react';
import ValidationResultsModal from './ValidationResultsModal';

interface Props {
  startup: Record<string, any>;
  onGenerateBlueprint: (results: Record<string, any>) => Promise<void>;
  pipelineStatus?: string;
  pipelineStep?: number;
}

const C = {
  bg: '#0d0d1f',
  surface: '#161627',
  surface2: '#13132a',
  border: '#2a2a3e',
  primary: '#7c6ff7',
  accent: '#00d4ff',
  green: '#22c55e',
  amber: '#f59e0b',
  danger: '#ff4466',
  text: '#f0f0ff',
  text2: '#8888aa',
  text3: '#444466',
};

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 6, color: C.text2, fontSize: 11, padding: '3px 8px', cursor: 'pointer' }}>
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

function CollapsibleSection({ title, icon, children, defaultOpen = false, copyText }: {
  title: string; icon: string; children: React.ReactNode; defaultOpen?: boolean; copyText?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
      <button onClick={() => setOpen(!open)} style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: C.surface2, border: 'none', padding: '14px 18px', cursor: 'pointer', textAlign: 'left',
      }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{icon} {title}</span>
        <span style={{ color: C.text3, fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding: 18, background: C.surface }}>
          {children}
          {copyText && (
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
              <CopyBtn text={copyText} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MVPBlueprint({ startup, onGenerateBlueprint, pipelineStatus, pipelineStep }: Props) {
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const printRef = useRef<HTMLDivElement>(null);

  const blueprint = startup.mvpBlueprint;
  const hasResults = !!startup.validationResults?.submittedAt;
  const isComplete = blueprint?.status === 'complete' && (blueprint?.data || blueprint?.buildPlan);

  const loadingSteps = [
    'Analyzing your validation results...',
    'Building your tech stack recommendation...',
    'Drafting your pitch deck outline...',
    'Finalizing your blueprint...',
  ];

  const handleGenerate = async (results: Record<string, any>) => {
    setShowResultsModal(false);
    setGenerating(true);
    setLoadingStep(0);
    const interval = setInterval(() => setLoadingStep(s => Math.min(s + 1, loadingSteps.length - 1)), 3000);
    try {
      await onGenerateBlueprint(results);
    } finally {
      clearInterval(interval);
      setGenerating(false);
    }
  };

  // STATE 1: Generating
  if (generating) {
    const progress = Math.min(((loadingStep + 1) / loadingSteps.length) * 100, 95);
    return (
      <div className="card" style={{ padding: 40, textAlign: 'center' }}>
        <div style={{
          width: 48, height: 48, margin: '0 auto 20px',
          borderRadius: '50%',
          background: `conic-gradient(from 0deg, ${C.primary}, ${C.accent}, rgba(124,111,247,0.1), ${C.primary})`,
          animation: 'spin 1.2s linear infinite', position: 'relative',
        }}>
          <div style={{ width: 34, height: 34, background: C.surface, borderRadius: '50%', position: 'absolute', top: 7, left: 7 }} />
        </div>
        <p style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 8 }}>Generating your personalized blueprint...</p>
        <p style={{ fontSize: 13, color: C.accent, marginBottom: 16 }}>{loadingSteps[loadingStep]}</p>
        <div style={{ width: '100%', maxWidth: 300, height: 4, background: C.surface2 || '#13132a', borderRadius: 4, overflow: 'hidden', margin: '0 auto' }}>
          <div style={{
            height: '100%', borderRadius: 4,
            background: `linear-gradient(90deg, ${C.primary}, ${C.accent})`,
            width: `${progress}%`,
            transition: 'width 2s ease',
          }} />
        </div>
        <p style={{ color: C.text3 || '#444466', fontSize: 11, marginTop: 8 }}>This may take up to 90 seconds...</p>
      </div>
    );
  }

  // UX 8: Locked state with specific checklist of what's blocking unlock
  if (!isComplete) {
    const pipelineDone = pipelineStatus === 'completed' || (pipelineStep ?? 0) >= 5;
    const conditions = [
      { label: 'Complete Pipeline Step 5: Validation', done: pipelineDone, link: '/pipeline' },
      { label: 'Submit validation results', done: hasResults, link: '' },
    ];
    const metCount = conditions.filter(c => c.done).length;

    return (
      <>
        <div className="card" style={{ padding: 32, border: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ fontSize: 36 }}><Hammer size={36} style={{ color: '#7c6ff7' }} /></div>
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                MVP Blueprint — Almost There
              </h3>
              <p style={{ fontSize: 13, color: C.text2 }}>
                Complete your 14-day experiment first — then we'll build your full MVP plan.
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ height: 4, background: C.surface2, borderRadius: 2, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{
              height: '100%', borderRadius: 2,
              background: `linear-gradient(90deg, ${C.primary}, ${C.accent})`,
              width: `${(metCount / conditions.length) * 100}%`,
              transition: 'width 0.5s ease',
            }} />
          </div>

          {/* Checklist */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
            {conditions.map((c, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                background: c.done ? `${C.green}08` : C.surface2,
                border: `1px solid ${c.done ? `${C.green}20` : C.border}`,
                borderRadius: 8,
              }}>
                <span style={{ fontSize: 16, color: c.done ? C.green : C.text3 }}>
                  {c.done ? <Check size={14} style={{color:'#22c55e'}} /> : <Square size={14} style={{color:'#4b5563'}} />}
                </span>
                <span style={{
                  flex: 1, fontSize: 13,
                  color: c.done ? C.text2 : C.text,
                  textDecoration: c.done ? 'line-through' : 'none',
                }}>
                  {c.label}
                </span>
                {!c.done && c.link && (
                  <a href={c.link} style={{
                    fontSize: 11, color: C.primary, background: `${C.primary}10`,
                    border: `1px solid ${C.primary}30`, borderRadius: 6,
                    padding: '3px 8px', textDecoration: 'none',
                  }}>
                    Go →
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* CTA — if pipeline done, offer to submit results */}
          {pipelineDone && (
            <button onClick={() => setShowResultsModal(true)} style={{
              width: '100%',
              background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
              border: 'none', borderRadius: 12, color: '#fff',
              fontSize: 14, fontWeight: 700, padding: '12px 24px', cursor: 'pointer',
            }}>
              I've run my experiment →
            </button>
          )}
        </div>
        <ValidationResultsModal isOpen={showResultsModal} onClose={() => setShowResultsModal(false)} onSubmit={handleGenerate} />
      </>
    );
  }

  // FIX: #9 — visual hierarchy badge for pre-validation vs post-validation blueprint
  const isBuiltOnRealData = hasResults;

  // STATE 3: Unlocked — render 3-layer blueprint
  const bp = blueprint.data as Record<string, any> ?? {};
  const build = bp.buildPlan ?? blueprint.buildPlan ?? {};
  const pitch = bp.pitchDeck ?? blueprint.pitchDeck ?? {};
  const hiring = bp.hiringBrief ?? blueprint.hiringBrief ?? {};
  const rec = bp.recommendation ?? 'build';
  const recColors: Record<string, string> = { build: C.green, pivot: C.amber, stop: C.danger };

  const buildText = JSON.stringify(build, null, 2);
  const pitchText = (pitch.slides ?? []).map((s: any) => `${s.title}\n${s.content}\nData: ${s.dataPoint}`).join('\n\n');
  const hiringText = JSON.stringify(hiring, null, 2);

  return (
    <>
      {/* FIX: #9 — source badge showing pre-validation vs real data */}
      <div style={{ marginBottom: 12 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, letterSpacing: '0.04em',
          background: isBuiltOnRealData ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.12)',
          color: isBuiltOnRealData ? '#22c55e' : '#f59e0b',
          border: `1px solid ${isBuiltOnRealData ? 'rgba(34,197,94,0.3)' : 'rgba(245,158,11,0.3)'}`,
        }}>
          {isBuiltOnRealData ? 'BUILT ON REAL DATA' : 'PRE-VALIDATION ESTIMATE'}
        </span>
        {!isBuiltOnRealData && (
          <span style={{ fontSize: 11, color: C.text3, marginLeft: 10 }}>
            Submit validation results to get a data-backed blueprint
          </span>
        )}
      </div>

      {/* Recommendation badge */}
      <div className="card" style={{ padding: 24, marginBottom: 12, borderLeft: `4px solid ${recColors[rec] ?? C.green}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <span style={{
            background: `${recColors[rec] ?? C.green}20`, color: recColors[rec] ?? C.green,
            fontSize: 14, fontWeight: 900, padding: '6px 16px', borderRadius: 8, textTransform: 'uppercase', letterSpacing: 1,
          }}>{rec}</span>
          <span style={{ fontSize: 13, color: C.text2 }}>AI Recommendation</span>
        </div>
        <p style={{ color: C.text, fontSize: 14, lineHeight: 1.6 }}>{bp.recommendationReason ?? ''}</p>
      </div>

      {/* LAYER 1: Build Plan */}
      <CollapsibleSection title="Build Plan" icon="" defaultOpen copyText={buildText}>
        {build.techStack && (
          <div style={{ marginBottom: 18 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: C.accent, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Tech Stack</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8, marginBottom: 10 }}>
              {[['Frontend', build.techStack.frontend], ['Backend', build.techStack.backend], ['Database', build.techStack.database]].map(([label, val]) => (
                <div key={String(label)} style={{ background: C.bg, borderRadius: 8, padding: '8px 12px', border: `1px solid ${C.border}` }}>
                  <p style={{ fontSize: 10, color: C.text3, marginBottom: 2 }}>{String(label)}</p>
                  <p style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{String(val ?? '')}</p>
                </div>
              ))}
            </div>
            {build.techStack.thirdParty?.length > 0 && (
              <p style={{ fontSize: 12, color: C.text2, marginBottom: 4 }}>
                <span style={{ color: C.text3 }}>3rd Party:</span> {build.techStack.thirdParty.join(', ')}
              </p>
            )}
            {build.techStack.reasoning && (
              <p style={{ fontSize: 12, color: C.text3, fontStyle: 'italic' }}>{build.techStack.reasoning}</p>
            )}
          </div>
        )}

        {build.features?.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: C.accent, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Features</p>
            {build.features.filter((f: any) => f.priority === 1 || f.buildFirst).map((f: any, i: number) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 12px', background: C.bg, borderRadius: 8, marginBottom: 6, border: `1px solid ${C.border}` }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, height: 'fit-content',
                  background: f.effort === 'low' ? `${C.green}20` : f.effort === 'medium' ? `${C.amber}20` : `${C.danger}20`,
                  color: f.effort === 'low' ? C.green : f.effort === 'medium' ? C.amber : C.danger,
                }}>{f.effort}</span>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{f.name}</p>
                  <p style={{ fontSize: 12, color: C.text2 }}>{f.description}</p>
                </div>
              </div>
            ))}
            {build.features.filter((f: any) => f.priority > 1 && !f.buildFirst).length > 0 && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ fontSize: 12, color: C.text3, cursor: 'pointer' }}>
                  + {build.features.filter((f: any) => f.priority > 1 && !f.buildFirst).length} lower-priority features
                </summary>
                {build.features.filter((f: any) => f.priority > 1 && !f.buildFirst).map((f: any, i: number) => (
                  <div key={i} style={{ display: 'flex', gap: 8, padding: '8px 12px', marginTop: 4, opacity: 0.7 }}>
                    <span style={{ fontSize: 10, color: C.text3 }}>P{f.priority}</span>
                    <span style={{ fontSize: 12, color: C.text2 }}>{f.name}</span>
                  </div>
                ))}
              </details>
            )}
          </div>
        )}

        {build.sprintPlan?.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: C.accent, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Sprint Plan</p>
            {build.sprintPlan.map((s: any) => (
              <div key={s.week} style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.primary, minWidth: 55 }}>Week {s.week}</span>
                <div>
                  <p style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{s.goal}</p>
                  <p style={{ fontSize: 12, color: C.text2 }}>→ {s.deliverable}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {build.costEstimate && (
          <div style={{ background: `${C.accent}08`, borderRadius: 10, padding: 14, border: `1px solid ${C.accent}20` }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: C.accent, marginBottom: 4 }}>Cost Estimate</p>
            <p style={{ fontSize: 18, fontWeight: 700, color: C.text }}>${build.costEstimate.low}–${build.costEstimate.high}</p>
            <p style={{ fontSize: 12, color: C.text2 }}>{build.costEstimate.breakdown}</p>
          </div>
        )}
      </CollapsibleSection>

      {/* LAYER 2: Pitch Deck */}
      <CollapsibleSection title="Pitch Deck Outline" icon="" copyText={pitchText}>
        {(pitch.slides ?? []).map((slide: any, i: number) => (
          <div key={i} style={{ padding: '12px 14px', borderLeft: `3px solid ${C.primary}`, marginBottom: 10, background: C.bg, borderRadius: '0 8px 8px 0' }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>Slide {i + 1}: {slide.title}</p>
            <p style={{ fontSize: 12, color: C.text2, marginBottom: 4 }}>{slide.content}</p>
            {slide.dataPoint && <p style={{ fontSize: 11, color: C.accent }}>{slide.dataPoint}</p>}
          </div>
        ))}
      </CollapsibleSection>

      {/* LAYER 3: Hiring Brief */}
      <CollapsibleSection title="First Hire" icon="" copyText={hiringText}>
        {hiring.needed === false ? (
          <p style={{ fontSize: 14, color: C.text2, lineHeight: 1.6 }}>
            No hire needed at this stage — you can build this solo with your technical background.
          </p>
        ) : (
          <div>
            <p style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 12 }}>{hiring.role}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: 12, marginBottom: 14 }}>
              <div>
                <p style={{ fontSize: 11, color: C.green, fontWeight: 700, marginBottom: 6 }}>Must Have</p>
                {(hiring.mustHave ?? []).map((s: string, i: number) => (
                  <p key={i} style={{ fontSize: 12, color: C.text2, marginBottom: 3 }}>✓ {s}</p>
                ))}
              </div>
              <div>
                <p style={{ fontSize: 11, color: C.text3, fontWeight: 700, marginBottom: 6 }}>Nice to Have</p>
                {(hiring.niceToHave ?? []).map((s: string, i: number) => (
                  <p key={i} style={{ fontSize: 12, color: C.text3, marginBottom: 3 }}>○ {s}</p>
                ))}
              </div>
            </div>
            {hiring.compensationRange && <p style={{ fontSize: 12, color: C.text2, marginBottom: 4 }}>{hiring.compensationRange}</p>}
            {hiring.whereToPost?.length > 0 && <p style={{ fontSize: 12, color: C.text2, marginBottom: 4 }}>Post on: {hiring.whereToPost.join(', ')}</p>}
            {hiring.interviewQuestion && (
              <div style={{ background: `${C.primary}0d`, borderRadius: 8, padding: 12, marginTop: 10 }}>
                <p style={{ fontSize: 11, color: C.primary, fontWeight: 700, marginBottom: 4 }}>Interview Question</p>
                <p style={{ fontSize: 13, color: C.text, fontStyle: 'italic' }}>"{hiring.interviewQuestion}"</p>
              </div>
            )}
          </div>
        )}
      </CollapsibleSection>

      {/* Print / Download */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
        <button onClick={() => {
          const el = document.getElementById('mvp-blueprint-print');
          if (el) { el.style.display = 'block'; window.print(); el.style.display = 'none'; }
        }} style={{
          background: 'transparent', border: `1px solid ${C.border}`,
          borderRadius: 10, color: C.text2, fontSize: 12, padding: '8px 18px', cursor: 'pointer',
        }}>
          ⬇ Download PDF
        </button>
      </div>

      {/* Print-only hidden div */}
      <div id="mvp-blueprint-print" ref={printRef} style={{ display: 'none' }} className="print-only">
        <h1>MVP Blueprint — {startup.name}</h1>
        <h2>Recommendation: {rec?.toUpperCase()}</h2>
        <p>{bp.recommendationReason}</p>
        <h2>Build Plan</h2>
        <pre>{buildText}</pre>
        <h2>Pitch Deck</h2>
        <pre>{pitchText}</pre>
        <h2>Hiring Brief</h2>
        <pre>{hiringText}</pre>
      </div>

      <ValidationResultsModal isOpen={showResultsModal} onClose={() => setShowResultsModal(false)} onSubmit={handleGenerate} />
    </>
  );
}
